import { useContext, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import SessionContext from '../../../library/Core/SessionContext';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import { DomainElement } from '../../../library/Core/SmartTypes';
import PlacementDataUpload from './PlacementDataUpload';
import PlacementInsights from './PlacementInsights';
import PlacementInsightDashboard from './PlacementInsightDashboard';
import PlacementAhliMatrix from './PlacementAhliMatrix';
import PlacementSpmMatrix from './PlacementSpmMatrix';
import PlacementActionables from './PlacementActionables';
import PlacementReconsiderationList from './PlacementReconsiderationList';
import PlacementAnalyticsService, { type PlacementTab } from './PlacementAnalyticsService';
import { MANAGEMENT_TABS, SUPPORT_TABS, instituteShellPath, listingPath, type PlacementRoleBase } from './deepLinks';
import './placementAnalytics.css';

interface Props {
    roleBase: PlacementRoleBase;
}

const PlacementInstituteShell = ({ roleBase }: Props) => {
    const { instituteId: routeInstituteId, tab: routeTab } = useParams();
    const { sessionState } = useContext(SessionContext);
    const { globalState } = useContext(GlobalContext);
    const sessionInstituteId = sessionState?.primaryInstitute || null;

    const instituteId = roleBase === 'support' ? routeInstituteId : sessionInstituteId;
    const tabs = roleBase === 'support' ? SUPPORT_TABS : MANAGEMENT_TABS;
    const defaultTab: PlacementTab = roleBase === 'support' ? 'data' : 'insights';
    const tab = ((routeTab as PlacementTab) || defaultTab) as PlacementTab;

    // Menu entry hits /management/placement-analytics with no tab — same rule as View Dashboard
    const needsLandingResolve = roleBase === 'management' && !routeTab;
    const [landingTab, setLandingTab] = useState<PlacementTab | null>(null);

    useEffect(() => {
        if (!needsLandingResolve || !instituteId) {
            setLandingTab(null);
            return;
        }
        let cancelled = false;
        setLandingTab(null);
        PlacementAnalyticsService.getInsightDashboard(instituteId)
            .then((res) => {
                if (!cancelled) setLandingTab(res.data?.uploadId ? 'summary' : 'insights');
            })
            .catch(() => {
                if (!cancelled) setLandingTab('insights');
            });
        return () => {
            cancelled = true;
        };
    }, [needsLandingResolve, instituteId]);

    const allowed = useMemo(() => new Set(tabs.map((t) => t.id)), [tabs]);

    const instituteName = useMemo(() => {
        if (!instituteId) return null;
        const domain = (globalState as any)?.domain as Map<string, DomainElement[]> | undefined;
        const hit = domain?.get('INSTITUTE_DOMAIN')?.find((e) => String(e.code) === String(instituteId));
        return hit?.value || null;
    }, [globalState, instituteId]);

    if (!instituteId) {
        return (
            <div className="main flex-1 p-4">
                <div className="alert alert-warning">No institute context available for Placement Analytics.</div>
            </div>
        );
    }

    if (needsLandingResolve) {
        if (!landingTab) {
            return <div className="main flex-1 p-4 text-muted font-13">Loading…</div>;
        }
        return <Navigate to={instituteShellPath('management', instituteId, landingTab)} replace />;
    }

    if (roleBase === 'management' && tab === 'data') {
        return <Navigate to={instituteShellPath('management', instituteId, 'insights')} replace />;
    }

    // Legacy URL from the old Insights-tab button
    if (tab === 'insights-dashboard') {
        return <Navigate to={instituteShellPath(roleBase, instituteId, 'summary')} replace />;
    }

    if (!allowed.has(tab)) {
        return <Navigate to={instituteShellPath(roleBase, instituteId, defaultTab)} replace />;
    }

    return (
        <div className="main flex-1">
            <div className="mb-4">
                <div className="white-block white-block-notopborderradius p-0 h-100">
                    <div className="p-4 p-md-5">
                        <div className="placement-shell-header">
                            <div className="placement-shell-title">
                                <h2 className="font-26 font-600 mb-1">Placement Analytics</h2>
                                <p className="placement-shell-subtitle">{instituteName || `Institute #${instituteId}`}</p>
                            </div>
                            {roleBase === 'support' && (
                                <Link to={listingPath()} className="placement-back-link">
                                    <span aria-hidden="true">‹</span>
                                    <span>Back to List</span>
                                </Link>
                            )}
                        </div>

                        <div className="placement-pill-bar">
                            {tabs.map((t) => (
                                <Link
                                    key={t.id}
                                    className={`placement-pill-link ${tab === t.id ? 'active' : ''}`}
                                    to={instituteShellPath(roleBase, instituteId, t.id)}>
                                    {t.label}
                                </Link>
                            ))}
                        </div>

                        {tab === 'data' && roleBase === 'support' && (
                            <PlacementDataUpload instituteId={instituteId} />
                        )}
                        {tab === 'summary' && (
                            <PlacementInsightDashboard
                                instituteId={instituteId}
                                roleBase={roleBase}
                                instituteName={instituteName}
                            />
                        )}
                        {tab === 'insights' && (
                            <PlacementInsights instituteId={instituteId} roleBase={roleBase} />
                        )}
                        {tab === 'ahli' && <PlacementAhliMatrix instituteId={instituteId} roleBase={roleBase} />}
                        {tab === 'spm' && <PlacementSpmMatrix instituteId={instituteId} roleBase={roleBase} />}
                        {tab === 'reconsideration' && (
                            <PlacementReconsiderationList instituteId={instituteId} roleBase={roleBase} />
                        )}
                        {tab === 'focus' && (
                            <PlacementActionables instituteId={instituteId} roleBase={roleBase} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlacementInstituteShell;
